import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {Bell, Plus, Search, Edit2, CheckCircle, Send, AlertTriangle, ExternalLink} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { useNotifications, NOTIF_TYPE_LABELS, AUDIENCE_LABELS, type AppNotification } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'
import { DUPLICATE_ALERT_PREFIX } from '@/lib/duplicateDetection'

const EMPTY: Omit<AppNotification, '_id'> = {
  title: '', body: '', type: 'aviso', targetAudience: 'todos',
  status: 'borrador', readBy: [], linkUrl: '',
  creator: 'admin', createdAt: '', updatedAt: '',
}

const STATUS_META: Record<AppNotification['status'], { label: string; color: string }> = {
  borrador: { label: 'Borrador', color: 'bg-slate-100 text-slate-600' },
  publicada: { label: 'Publicada', color: 'bg-emerald-100 text-emerald-700' },
  archivada: { label: 'Archivada', color: 'bg-blue-100 text-blue-600' },
}

function AdminNotificaciones() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { notifications, loading, fetchAll, createNotification, updateNotification } = useNotifications()
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('todos')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<AppNotification | null>(null)
  const [form, setForm] = useState<Omit<AppNotification, '_id'>>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => { fetchAll() }, [])

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, creator: user?.userId ?? 'admin' }); setShowModal(true) }
  const openEdit = (n: AppNotification) => { setEditing(n); setForm({ ...n }); setShowModal(true) }

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) return
    setSaving(true)
    try {
      if (editing) {
        await updateNotification(editing._id, form)
        showToast('Comunicado actualizado.')
      } else {
        await createNotification(form)
        showToast('Comunicado creado.')
      }
      await fetchAll()
      setShowModal(false)
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const handlePublish = async (n: AppNotification) => {
    try {
      await updateNotification(n._id, { status: 'publicada' })
      await fetchAll()
      showToast(`"${n.title}" publicado correctamente.`)
    } catch (e) { console.error(e) }
  }

  const handleArchive = async (n: AppNotification) => {
    try {
      await updateNotification(n._id, { status: 'archivada' })
      await fetchAll()
      showToast('Comunicado archivado.')
    } catch (e) { console.error(e) }
  }

  const filtered = notifications.filter((n) => {
    const matchSearch = !search || n.title.toLowerCase().includes(search.toLowerCase())
    const matchStatus = filterStatus === 'todos' || n.status === filterStatus
    return matchSearch && matchStatus
  })

  return (
    <AdminLayout title="Comunicados" subtitle="Envía avisos segmentados a empresas, colaboradores o ambos">
      {toast ? (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm text-white shadow-lg">
          <CheckCircle size={16} className="text-emerald-400" />{toast}
        </div>
      ) : null}

          <div className="mb-6 flex justify-end">
            <button onClick={openCreate} className="flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-500">
              <Plus size={16} /> Nuevo comunicado
            </button>
          </div>

          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100" />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400">
              <option value="todos">Todos los estados</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <Bell size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin comunicados</p>
              <button onClick={openCreate} className="mt-4 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-500">Crear primer comunicado</button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((n) => {
                const meta = STATUS_META[n.status]
                const isDuplicate = n.title.startsWith(DUPLICATE_ALERT_PREFIX)
                return (
                  <div key={n._id} className={`flex items-start gap-4 rounded-xl border bg-white p-4 ${isDuplicate ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        {isDuplicate ? (
                          <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                            <AlertTriangle size={11} /> Revisión de duplicado
                          </span>
                        ) : null}
                        <p className="text-sm font-semibold text-slate-900">{n.title.replace(DUPLICATE_ALERT_PREFIX, '').trim()}</p>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{NOTIF_TYPE_LABELS[n.type]}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Para: {AUDIENCE_LABELS[n.targetAudience]}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{n.body}</p>
                      <p className="mt-1 text-xs text-slate-400">{n.createdAt.slice(0, 10)} · {n.readBy?.length ?? 0} lecturas</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {isDuplicate && n.linkUrl ? (
                        <button onClick={() => navigate(n.linkUrl as string)} className="flex items-center gap-1 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-amber-500">
                          <ExternalLink size={12} /> Revisar registro
                        </button>
                      ) : null}
                      {n.status === 'borrador' ? (
                        <button onClick={() => handlePublish(n)} className="flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100">
                          <Send size={12} /> Publicar
                        </button>
                      ) : n.status === 'publicada' ? (
                        <button onClick={() => handleArchive(n)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                          Archivar
                        </button>
                      ) : null}
                      <button onClick={() => openEdit(n)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Edit2 size={14} /></button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar comunicado' : 'Nuevo comunicado'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Título *</label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Contenido *</label>
                <textarea rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Tipo</label>
                  <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AppNotification['type'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(NOTIF_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Destinatarios</label>
                  <select value={form.targetAudience} onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value as AppNotification['targetAudience'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(AUDIENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Estado</label>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as AppNotification['status'] }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400">
                    {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Enlace (opcional)</label>
                  <input value={form.linkUrl ?? ''} onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                    placeholder="/colaborador/capacitaciones"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-amber-400" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.body.trim()} className="rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-500 disabled:opacity-60">
                {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear comunicado'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  )
}

export default AdminNotificaciones
